import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { IMAGES, bgStyle } from '@/config/images';
import { 
  Home, 
  Calculator, 
  Settings2, 
  Printer, 
  AlertTriangle, 
  ArrowRight,
  CheckCircle,
  Zap,
  Target,
  RefreshCw,
  ChevronRight,
  Thermometer,
  Layers,
  Box,
  Wrench,
  BookOpen
} from 'lucide-react';

const Print3DIntroduction = () => {
  const navigate = useNavigate();

  const handleProceed = () => {
    navigate('/3d-printing');
  };

  return (
    <div 
      data-testid="print3d-introduction-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.GETTING_STARTED)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      <div className="max-w-4xl mx-auto space-y-8 pb-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          3D Printing Helpdesk
        </h1>
        <p className="text-xl md:text-2xl text-[#e67e22] font-semibold">
          Get perfect prints – every layer, every time.
        </p>
      </div>

      {/* Intro Description */}
      <div className="bg-[#2c2c2c]/80 border border-[#3a3a3a] rounded-sm p-6 space-y-4">
        <p className="text-base md:text-lg text-[#d6d6d6] leading-relaxed">
          CNC-Helpdesk 3D Printing Edition is your control centre for temperature settings, print speeds, troubleshooting failed prints, and optimizing your slicer settings.
        </p>
        <p className="text-base md:text-lg text-[#a3a3a3] leading-relaxed">
          No more guessing or endless forum searches – it's a <span className="text-[#e67e22] font-semibold">simple workflow</span> you can follow for every print.
        </p>
      </div>

      {/* How to Get Set Up */}
      <div className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-wide flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Target className="w-7 h-7 text-[#e67e22]" />
          How to Get Set Up (Step by Step)
        </h2>

        {/* Step 1 - Dashboard */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">1</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Start on the Dashboard</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">This is your home base for 3D printing.</p>
          <p className="text-[#d6d6d6] ml-13">From here you can jump to:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-13">
            <Link to="/3d-printing/calculator" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <Calculator className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">Print Calculator</span>
              <span className="text-[#a3a3a3]">– get optimal settings</span>
            </Link>
            <Link to="/3d-printing/setup" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <Printer className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">Printer Setup</span>
              <span className="text-[#a3a3a3]">– configure your machine</span>
            </Link>
            <Link to="/3d-printing/materials" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <BookOpen className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">Material Guide</span>
              <span className="text-[#a3a3a3]">– learn about filaments</span>
            </Link>
            <Link to="/3d-printing/troubleshooting" className="flex items-center gap-2 text-sm hover:bg-[#2c2c2c] p-1 rounded transition-colors">
              <AlertTriangle className="w-4 h-4 text-[#e67e22]" />
              <span className="text-[#e67e22] font-semibold hover:underline">Troubleshooting</span>
              <span className="text-[#a3a3a3]">– fix print issues</span>
            </Link>
          </div>
          <p className="text-[#d6d6d6] ml-13 text-sm italic border-l-2 border-[#e67e22] pl-3 mt-3">
            If you're new to 3D printing, follow the steps below in order.
          </p>
        </div>

        {/* Step 2 - Printer Setup */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">2</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Configure Your Printer (Printer Setup)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">Go to <Link to="/3d-printing/setup" className="text-[#e67e22] font-semibold hover:underline">Printer Setup</Link> and add your 3D printer:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Select your <span className="text-[#e67e22]">printer brand and model</span> (Creality, Prusa, Bambu Lab, etc.)
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Or create a <span className="text-[#e67e22]">custom printer profile</span> for DIY/modified machines
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Set your <span className="text-[#e67e22]">build volume</span>, <span className="text-[#e67e22]">nozzle size</span>, and <span className="text-[#e67e22]">heated bed</span> capabilities
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Choose your <span className="text-[#e67e22]">slicer software</span> (Cura, PrusaSlicer, OrcaSlicer, etc.)
            </li>
          </ul>
          <div className="ml-13 bg-[#1a1a1a] border border-[#e67e22]/30 rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">CNC-Helpdesk uses this profile to:</p>
            <ul className="space-y-1 text-sm text-[#a3a3a3]">
              <li className="flex items-center gap-2"><Zap className="w-3 h-3 text-[#e67e22]" /> Recommend settings suited to your hardware</li>
              <li className="flex items-center gap-2"><Zap className="w-3 h-3 text-[#e67e22]" /> Account for bed size and max temperatures</li>
              <li className="flex items-center gap-2"><Zap className="w-3 h-3 text-[#e67e22]" /> Provide slicer-specific troubleshooting advice</li>
            </ul>
          </div>
        </div>

        {/* Step 3 - Materials */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">3</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Learn Your Materials (Material Guide)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">Different filaments need different settings. Check the <Link to="/3d-printing/materials" className="text-[#e67e22] font-semibold hover:underline">Material Guide</Link> for:</p>
          <div className="ml-13 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="w-4 h-4 text-[#e67e22]" />
                <span className="text-[#e67e22] font-semibold text-sm">PLA</span>
              </div>
              <p className="text-xs text-[#a3a3a3]">Easy to print, low temp, great for beginners</p>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="w-4 h-4 text-[#e67e22]" />
                <span className="text-[#e67e22] font-semibold text-sm">PETG</span>
              </div>
              <p className="text-xs text-[#a3a3a3]">Strong, flexible, good layer adhesion</p>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="w-4 h-4 text-[#e67e22]" />
                <span className="text-[#e67e22] font-semibold text-sm">ABS</span>
              </div>
              <p className="text-xs text-[#a3a3a3]">Heat resistant, needs enclosure</p>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="w-4 h-4 text-[#e67e22]" />
                <span className="text-[#e67e22] font-semibold text-sm">TPU</span>
              </div>
              <p className="text-xs text-[#a3a3a3]">Flexible, rubber-like, slow print speeds</p>
            </div>
          </div>
          <p className="text-[#d6d6d6] ml-13 mt-3 text-sm">Each material page shows <span className="text-[#e67e22]">temperature ranges</span>, <span className="text-[#e67e22]">bed adhesion tips</span>, and <span className="text-[#e67e22]">common issues</span>.</p>
        </div>

        {/* Step 4 - Calculator */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">4</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Get Your Print Settings (Print Calculator)</h3>
          </div>
          <p className="text-[#e67e22] ml-13 font-semibold">Now the fun part.</p>
          <p className="text-[#a3a3a3] ml-13">Open the <Link to="/3d-printing/calculator" className="text-[#e67e22] font-semibold hover:underline">Print Calculator</Link> and:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Select your <span className="text-[#e67e22]">filament type</span> (PLA, PETG, ABS, etc.)
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Choose your <span className="text-[#e67e22]">print quality</span> (Draft, Standard, Fine)
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Specify your <span className="text-[#e67e22]">nozzle size</span>
            </li>
          </ul>
          <p className="text-[#d6d6d6] ml-13 mt-3">Get recommended settings for:</p>
          <div className="ml-13 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <Thermometer className="w-4 h-4 text-[#e67e22] mx-auto mb-1" />
              <span className="text-[#e67e22] font-semibold text-xs">Nozzle Temp</span>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <Thermometer className="w-4 h-4 text-[#e67e22] mx-auto mb-1" />
              <span className="text-[#e67e22] font-semibold text-xs">Bed Temp</span>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <Zap className="w-4 h-4 text-[#e67e22] mx-auto mb-1" />
              <span className="text-[#e67e22] font-semibold text-xs">Print Speed</span>
            </div>
            <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm py-2 px-3">
              <Layers className="w-4 h-4 text-[#e67e22] mx-auto mb-1" />
              <span className="text-[#e67e22] font-semibold text-xs">Layer Height</span>
            </div>
          </div>
          <div className="ml-13 bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">Additional settings include:</p>
            <ul className="space-y-1 text-sm text-[#a3a3a3]">
              <li>• Retraction distance and speed</li>
              <li>• Cooling fan percentage</li>
              <li>• First layer settings</li>
              <li>• Infill recommendations</li>
            </ul>
          </div>
        </div>

        {/* Step 5 - Troubleshooting */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">5</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Fix Print Problems (Troubleshooting)</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">If your print doesn't look right:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Go to <Link to="/3d-printing/troubleshooting" className="text-[#e67e22] font-semibold hover:underline">Troubleshooting</Link>
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Select your issue: stringing, warping, layer separation, etc.
            </li>
            <li className="flex items-start gap-2 text-[#d6d6d6]">
              <ChevronRight className="w-4 h-4 text-[#e67e22] mt-0.5" />
              Choose your printer and slicer for tailored advice
            </li>
          </ul>
          <div className="ml-13 bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-4 mt-3">
            <p className="text-[#d6d6d6] font-semibold mb-2">Common issues we help fix:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-[#a3a3a3]">
                <AlertTriangle className="w-3 h-3 text-[#f39c12]" />
                Stringing & oozing
              </div>
              <div className="flex items-center gap-2 text-[#a3a3a3]">
                <AlertTriangle className="w-3 h-3 text-[#f39c12]" />
                Warping & lifting
              </div>
              <div className="flex items-center gap-2 text-[#a3a3a3]">
                <AlertTriangle className="w-3 h-3 text-[#f39c12]" />
                Poor bed adhesion
              </div>
              <div className="flex items-center gap-2 text-[#a3a3a3]">
                <AlertTriangle className="w-3 h-3 text-[#f39c12]" />
                Layer shifting
              </div>
              <div className="flex items-center gap-2 text-[#a3a3a3]">
                <AlertTriangle className="w-3 h-3 text-[#f39c12]" />
                Under/over extrusion
              </div>
              <div className="flex items-center gap-2 text-[#a3a3a3]">
                <AlertTriangle className="w-3 h-3 text-[#f39c12]" />
                Clogged nozzle
              </div>
            </div>
          </div>
          <div className="ml-13 bg-[#1a1a1a] border border-[#27ae60]/30 rounded-sm p-4 mt-3">
            <p className="text-[#27ae60] font-semibold mb-2">AI-Powered Help</p>
            <p className="text-sm text-[#a3a3a3]">Can't find your issue? Use our <Link to="/3d-printing/ask-ai" className="text-[#e67e22] hover:underline">AI Assistant</Link> to describe your problem and get personalized solutions based on your specific printer and setup.</p>
          </div>
        </div>

        {/* Step 6 - First Print */}
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-5 space-y-4 hover:border-[#e67e22]/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e67e22] rounded-full flex items-center justify-center text-white font-bold text-lg">6</div>
            <h3 className="text-lg md:text-xl font-bold text-white">Your First Print Checklist</h3>
          </div>
          <p className="text-[#a3a3a3] ml-13">Before hitting print, make sure you've:</p>
          <ul className="ml-13 space-y-2">
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Leveled your bed (or run auto bed leveling)
            </li>
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Cleaned the build surface
            </li>
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Loaded filament and done a purge line
            </li>
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Set the correct temperatures for your material
            </li>
            <li className="flex items-center gap-2 text-[#d6d6d6]">
              <CheckCircle className="w-4 h-4 text-[#27ae60]" />
              Watched the first layer go down successfully
            </li>
          </ul>
        </div>

      </div>

      {/* Your New 3D Printing Routine */}
      <div className="bg-[#2c2c2c]/80 border-2 border-[#e67e22]/50 rounded-sm p-6 space-y-4">
        <h2 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-wide flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <RefreshCw className="w-7 h-7 text-[#e67e22]" />
          Your New 3D Printing Routine
        </h2>
        <p className="text-[#d6d6d6]">Every print can follow the same simple loop:</p>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">1</div>
            <div className="text-xs text-[#a3a3a3]">Check printer & filament</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">2</div>
            <div className="text-xs text-[#a3a3a3]">Get settings from calculator</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">3</div>
            <div className="text-xs text-[#a3a3a3]">Slice & export G-code</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#3a3a3a] rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">4</div>
            <div className="text-xs text-[#a3a3a3]">Print</div>
          </div>
          <div className="bg-[#1a1a1a] border border-[#e67e22]/30 rounded-sm p-3 text-center">
            <div className="text-[#e67e22] font-bold text-lg">5</div>
            <div className="text-xs text-[#a3a3a3]">If issues → Troubleshoot</div>
          </div>
        </div>
        <p className="text-[#d6d6d6] text-center mt-4 text-lg font-medium">
          CNC-Helpdesk 3D Printing is built to make your prints <span className="text-[#e67e22]">cleaner</span>, <span className="text-[#e67e22]">faster</span>, and <span className="text-[#e67e22]">more reliable</span> – without endless trial and error.
        </p>
      </div>

      {/* CTA */}
      <div className="text-center pt-4">
        <Button
          onClick={handleProceed}
          data-testid="get-started-btn"
          className="bg-[#e67e22] hover:bg-[#d35400] text-white font-bold px-8 py-3 text-base uppercase tracking-wide shadow-[0_0_15px_rgba(230,126,34,0.4)] hover:shadow-[0_0_25px_rgba(230,126,34,0.5)]"
        >
          Go to Dashboard
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
      </div>
    </div>
  );
};

export default Print3DIntroduction;
